import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import restaurantLogo from '../assets/restaurant-logo.png';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, { threshold: 0.1 });
    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small restaurants getting started',
      price: { monthly: 999, annual: 9990 },
      features: [
        'Up to 50 menu items',
        'QR code ordering',
        'Basic analytics',
        'Payment integration',
        'Email support',
        '1 restaurant location'
      ],
      popular: false
    },
    {
      name: 'Professional',
      description: 'Most popular choice for growing restaurants',
      price: { monthly: 1999, annual: 19990 },
      features: [
        'Unlimited menu items',
        'Advanced QR ordering',
        'Real-time analytics',
        'Multiple payment gateways',
        'Priority support',
        'Up to 3 locations',
        'Staff management',
        'Custom branding'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      description: 'For large chains and franchises',
      price: { monthly: 4999, annual: 49990 },
      features: [
        'Everything in Professional',
        'Unlimited locations',
        'Advanced reporting',
        'Dedicated account manager',
        '24/7 phone support',
        'Custom integrations'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-sans selection:bg-[#F5A623] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#0D0D0D]/90 py-4 px-6 border-b border-white/10 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
          </Link>
          <Link to="/" className="text-sm font-bold text-gray-400 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeftIcon className="w-4 h-4" />
            Back Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6">
        <div className="max-w-4xl mx-auto text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out">
          <div className="inline-block border border-[#F5A623]/30 bg-[#F5A623]/5 rounded-full px-4 py-1.5 mb-8 text-xs font-bold tracking-wider text-[#F5A623]">
            TRANSPARENT PRICING
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 leading-none">
            Scale your <span className="text-white">revenue,</span><br/>not your <span className="text-gray-500">costs.</span>
          </h1>
          
          <div className="flex items-center justify-center gap-4 mt-12 mb-16">
            <span className={`text-sm font-bold ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
            <button 
              onClick={() => setIsAnnual(!isAnnual)}
              className="w-14 h-7 bg-white/5 border border-white/20 rounded-full relative transition-colors hover:border-white/40"
            >
              <div className={`absolute top-1 w-5 h-5 bg-[#F5A623] rounded-full transition-all duration-300 ${isAnnual ? 'left-8' : 'left-1'}`} />
            </button>
            <span className={`text-sm font-bold ${isAnnual ? 'text-white' : 'text-gray-500'}`}>Annual <span className="text-[#F5A623] ml-1">(Save 17%)</span></span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8 px-6">
          {plans.map((plan, i) => (
            <div 
              key={i} 
              className={`reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out p-8 rounded-2xl border ${plan.popular ? 'bg-[#111111] border-[#F5A623]/50 shadow-[0_0_40px_rgba(245,166,35,0.1)]' : 'bg-[#0A0A0A] border-white/10'} flex flex-col`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm font-light mb-6 leading-relaxed">{plan.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black">₹{isAnnual ? Math.floor(plan.price.annual / 12) : plan.price.monthly}</span>
                  <span className="text-gray-500 text-sm">/mo</span>
                </div>
                {isAnnual && <div className="text-xs text-[#F5A623] mt-2 font-bold">Billed ₹{plan.price.annual} annually</div>}
              </div>

              <div className="space-y-4 mb-10 flex-1">
                {plan.features.map((feature, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <CheckIcon className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-400 font-light">{feature}</span>
                  </div>
                ))}
              </div>

              <Link 
                to="/auth" 
                className={`w-full py-4 rounded-full text-center text-sm font-bold transition-all ${plan.popular ? 'bg-[#F5A623] text-black hover:bg-[#E0961E]' : 'bg-white/5 border border-white/20 text-white hover:bg-white/10'}`}
              >
                {plan.popular ? 'Start Free Trial' : 'Get Started'}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-16 text-center">Frequently Asked Questions</h2>
          <div className="grid gap-8">
            {[
              { q: "Can I change my plan anytime?", a: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle." },
              { q: "Is there a setup fee?", a: "No, there are no setup fees. You only pay the monthly or annual subscription fee." },
              { q: "What payment methods do you accept?", a: "We accept all major credit cards, debit cards, UPI, and bank transfers via our secure payment gateway." },
              { q: "Do you offer refunds?", a: "We focus on a 'Try Before You Buy' model with our 14-day free trial. If you cancel during the trial, you won't be charged." }
            ].map((faq, i) => (
              <div key={i} className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out border-b border-white/5 pb-8">
                <h3 className="text-lg font-bold mb-4 text-white">{faq.q}</h3>
                <p className="text-gray-500 font-light leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] pt-20 pb-10 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
          <div>
            <div className="mb-4">
              <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain" />
            </div>
            <p className="text-sm text-gray-500 max-w-xs font-light">
              Premium QR ordering and management platform for modern restaurants.
            </p>
          </div>
          <div className="flex gap-8 md:gap-16">
            <Link to="/features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</Link>
            <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/help" className="text-sm text-gray-400 hover:text-white transition-colors">Support</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/10 text-center text-xs text-gray-600">
          <p>&copy; {new Date().getFullYear()} Ordyrr. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
